import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useUnreadCount, useNotifications } from '../../hooks/useUserExtras';

/**
 * Bell icon for the navbar. Shows unread count via cheap poll
 * (GET /me/notifications/unread-count every 60s while page is open).
 * Click expands a dropdown that lazy-loads the full list once.
 */
export default function NotificationBell() {
  const { unread, refetch: refetchUnread, setUnread } = useUnreadCount(60_000);
  const [open, setOpen]           = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const ref = useRef(null);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(v => !v); if (!hasOpened) setHasOpened(true); }}
        aria-label="Notifications"
        style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'transparent', border: '1.5px solid #E4E7F0',
          cursor: 'pointer', position: 'relative', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', fontSize: 16,
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#F7F8FC'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 18, height: 18, padding: '0 4px',
            borderRadius: 999, background: '#DC2626', color: '#fff',
            fontSize: 10, fontWeight: 700, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff', fontFamily: 'system-ui, sans-serif',
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <NotificationDropdown
          onClose={() => setOpen(false)}
          onAllRead={() => { setUnread(0); refetchUnread(); }}
        />
      )}
    </div>
  );
}

function NotificationDropdown({ onClose, onAllRead }) {
  const { notifications, unread, loading, markRead, markAllRead } = useNotifications();

  const handleClick = async (n) => {
    if (!n.read_at) await markRead(n.id);
    if (n.cta_url) {
      // Strip any absolute-URL prefix so we stay on the current origin.
      // Without this, an old notification with cta_url=https://www.perkvora.com/profile
      // would yank a user signed in on dev.perkvora.com over to the public
      // production launch page (which serves the Coming Soon screen behind
      // the gate). Relative navigation keeps the user on their actual
      // session domain.
      const path = String(n.cta_url).replace(/^https?:\/\/[^/]+/i, '') || '/';
      window.location.assign(path);
    } else {
      onClose();
    }
  };

  const handleMarkAll = async () => {
    if (unread === 0) return;
    await markAllRead();
    onAllRead?.();
  };

  return (
    <div style={{
      position: 'absolute', top: 'calc(100% + 8px)', right: 0,
      width: 360, maxHeight: 480, overflow: 'hidden',
      background: '#fff', border: '1px solid #E4E7F0', borderRadius: 14,
      boxShadow: '0 12px 32px rgba(15,18,37,0.12)', zIndex: 200,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #E4E7F0' }}>
        <p style={{ fontWeight: 700, fontSize: 14 }}>
          Notifications {unread > 0 && <span style={{ color: '#DC2626', fontSize: 12, fontWeight: 600 }}>· {unread} unread</span>}
        </p>
        {unread > 0 && (
          <button onClick={handleMarkAll}
            style={{ background: 'none', border: 'none', color: '#6B2FD9', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
            Mark all read
          </button>
        )}
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#8B94A8' }}>Loading…</div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
            <p style={{ fontSize: 13, color: '#8B94A8' }}>You're all caught up.</p>
          </div>
        ) : notifications.map(n => (
          <button
            key={n.id}
            onClick={() => handleClick(n)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '12px 16px', border: 'none', background: n.read_at ? 'transparent' : '#F3EEFF',
              borderBottom: '1px solid #F0F2F8', cursor: 'pointer', fontFamily: 'inherit',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = n.read_at ? '#F7F8FC' : '#EBE0FF'; }}
            onMouseLeave={e => { e.currentTarget.style.background = n.read_at ? 'transparent' : '#F3EEFF'; }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              {!n.read_at && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#DC2626', flexShrink: 0, marginTop: 5 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: n.read_at ? 500 : 700, color: '#0F1225', lineHeight: 1.4 }}>{n.subject || n.event_key}</p>
                {n.body && <p style={{ fontSize: 12, color: '#4A5568', marginTop: 3, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.body}</p>}
                <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 4 }}>{relativeTime(n.created_at)}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div style={{ borderTop: '1px solid #E4E7F0', padding: 10 }}>
        <Link to="/profile/settings" onClick={onClose}
          style={{ display: 'block', textAlign: 'center', fontSize: 12, color: '#6B2FD9', fontWeight: 600, padding: 8, textDecoration: 'none' }}>
          Notification settings
        </Link>
      </div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60)    return 'just now';
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}
